import { isEqual, reject, sortBy } from "lodash/fp";
import { create } from "zustand";

import type { ConferencingMeetingPlatform } from "@app/common/types";

import type { CalendarFilter, ConferencingFilter } from "../types";

interface CalendarStateValues {
  conferencing: ConferencingFilter;
  calendars: {
    allSelected: boolean;
    all: CalendarFilter[];
    filters: CalendarFilter[];
  };
  platforms: {
    allSelected: boolean;
    all: ConferencingMeetingPlatform[];
    filters: ConferencingMeetingPlatform[];
  };
}

const DEFAULT_STATE_VALUES: CalendarStateValues = {
  conferencing: "conferencing_only",
  calendars: {
    allSelected: true,
    all: [],
    filters: [],
  },
  platforms: {
    allSelected: true,
    all: [],
    filters: [],
  },
};

interface CalendarState extends CalendarStateValues {
  setAllCalendars: (calendars: CalendarFilter[]) => void;
  setAllPlatforms: (platforms: ConferencingMeetingPlatform[]) => void;
  setConferencing: (value: ConferencingFilter) => void;
  toggleAllCalendars: (on: boolean) => void;
  toggleAllPlatforms: (on: boolean) => void;
  toggleCalendar: (calendar: CalendarFilter, on: boolean) => void;
  togglePlatform: (platform: ConferencingMeetingPlatform, on: boolean) => void;
}

const useCalendarStore = create<CalendarState>((set) => ({
  ...DEFAULT_STATE_VALUES,

  setAllCalendars: (calendars) =>
    set({
      calendars: {
        allSelected: true,
        all: calendars,
        filters: calendars,
      },
    }),
  setAllPlatforms: (platforms) =>
    set({
      platforms: {
        allSelected: true,
        all: platforms,
        filters: platforms,
      },
    }),
  setConferencing: (conferencing) => set({ conferencing }),
  toggleAllCalendars: (on) =>
    set((state) => ({
      calendars: {
        allSelected: on,
        all: state.calendars.all,
        filters: on ? state.calendars.all : [],
      },
    })),
  toggleAllPlatforms: (on) =>
    set((state) => ({
      platforms: {
        allSelected: on,
        all: state.platforms.all,
        filters: on ? state.platforms.all : [],
      },
    })),
  toggleCalendar: (calendar, on) =>
    set((state) => {
      const updatedFilters = on
        ? [...state.calendars.filters, calendar]
        : reject((c) => isEqual(c, calendar), state.calendars.filters);

      const sortedFilters = sortBy(
        ["provider", "subscriberEmail"],
        updatedFilters
      );
      const allSelected = isEqual(sortedFilters, state.calendars.all);

      return {
        calendars: {
          allSelected,
          all: state.calendars.all,
          filters: updatedFilters,
        },
      };
    }),
  togglePlatform: (platform, on) =>
    set((state) => {
      const updatedFilters = on
        ? [...state.platforms.filters, platform]
        : reject((p) => p === platform, state.platforms.filters);

      const sortedFilters = updatedFilters.sort();
      const allSelected = isEqual(sortedFilters, state.platforms.all);

      return {
        platforms: {
          allSelected,
          all: state.platforms.all,
          filters: updatedFilters,
        },
      };
    }),
}));

export default useCalendarStore;
